/**
 * ChangePasswordForm Component
 *
 * Lets a logged-in user change their own password. Verifies the current
 * password server-side, enforces strength rules, and (server-side) revokes the
 * user's other sessions while keeping the current one.
 */

import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useChangePassword } from '@/features/settings/hooks/useSettings';

const MIN_LENGTH = 12;

export const ChangePasswordForm: React.FC = () => {
  const { toast } = useToast();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (newPassword.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters long.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current password.');
      return;
    }

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      reset();
      toast({
        title: 'Password changed',
        description:
          'Your password has been updated. Other devices have been signed out.',
      });
    } catch (err: any) {
      // ApiError.message is already extracted from the server response
      // (e.g. "Current password is incorrect").
      setError(err?.message || 'Failed to change password. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="current-password">Current password</Label>
        <Input
          id="current-password"
          type={showPasswords ? 'text' : 'password'}
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-password">New password</Label>
        <Input
          id="new-password"
          type={showPasswords ? 'text' : 'password'}
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <p className="text-sm text-muted-foreground">
          At least {MIN_LENGTH} characters, with uppercase, lowercase, a digit,
          and a special character.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm-password">Confirm new password</Label>
        <Input
          id="confirm-password"
          type={showPasswords ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowPasswords((v) => !v)}
        >
          {showPasswords ? (
            <>
              <EyeOff className="h-4 w-4 mr-2" /> Hide
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 mr-2" /> Show
            </>
          )}
        </Button>

        <Button type="submit" disabled={changePassword.isPending}>
          {changePassword.isPending ? 'Changing...' : 'Change password'}
        </Button>
      </div>
    </form>
  );
};
